import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { invalidateQuery } from '@/hooks/useOptimizedQuery';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: any | null;
  loading: boolean;
  signUp: (email: string, password: string, fullName: string, userType: 'job_seeker' | 'employer', referralCode?: string, noticePeriod?: string, captchaToken?: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string, captchaToken?: string) => Promise<{ error: any }>;
  signInWithGoogle: () => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  generateReferralCode: () => Promise<{ code?: string; error: any }>;
  getReferralCodes: () => Promise<{ codes?: any[]; error: any }>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = useCallback(async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();
      
      if (error) {
        console.error('Error fetching profile:', error);
        return;
      }
      
      setProfile(data);
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  }, []);

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          // Only fetch profile if user is email confirmed
          if (session.user.email_confirmed_at) {
            setTimeout(() => {
              fetchProfile(session.user.id);
            }, 0);
          } else {
            setProfile(null);
          }
        } else {
          setProfile(null);
        }
        
        setLoading(false);
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user && session.user.email_confirmed_at) {
        fetchProfile(session.user.id);
      }
      
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [fetchProfile]);

  const signUp = async (email: string, password: string, fullName: string, userType: 'job_seeker' | 'employer', referralCode?: string, noticePeriod?: string, captchaToken?: string) => {
    // Check rate limiting first
    try {
      const { data: rateLimitData, error: rateLimitError } = await supabase.functions.invoke('auth-rate-limiter', {
        body: { email, action: 'signup' }
      });

      if (rateLimitError || (rateLimitData && rateLimitData.blocked)) {
        return { error: { message: rateLimitData?.error || 'Rate limit exceeded. Please try again later.' } };
      }
    } catch (rateLimitError) {
      console.warn('Rate limiter unavailable, proceeding with signup:', rateLimitError);
    }
    
    try {
      // Use custom edge function to create user without triggering Supabase's automatic emails
      const { data, error } = await supabase.functions.invoke('create-user-without-email', {
        body: {
          email,
          password,
          fullName,
          userType,
          referralCode,
          noticePeriod,
          captchaToken
        }
      });

      if (error) {
        console.error('Edge function error:', error);
        return { error: { message: error.message || 'Failed to create account. Please try again.' } };
      }

      if (data?.error) {
        console.error('Edge function returned error:', data.error);
        // Handle both string and object error formats
        const errorMessage = typeof data.error === 'string' ? data.error : 
                           data.error?.message || data.error || 
                           'Failed to create account. Please try again.';
        return { error: { message: errorMessage } };
      }

      // The edge function handles sending our custom confirmation email
      console.log('User signup initiated successfully');

      // Process referral code if provided and user creation was successful
      if (referralCode && data?.user?.id) {
        console.log('🎁 Processing referral code:', referralCode);
        try {
          const { data: functionData, error: functionError } = await supabase.rpc('process_referral_signup', {
            referral_code_text: referralCode,
            new_user_id: data.user.id
          });
          if (functionError) {
            console.error('❌ Error processing referral:', functionError);
          } else {
            console.log('✅ Referral processed successfully');
          }
        } catch (referralError) {
          console.error('❌ Exception processing referral:', referralError);
        }
      }

      return { error: null };
    } catch (edgeFunctionError) {
      console.error('❌ Exception calling edge function:', edgeFunctionError);
      return { error: { message: 'Failed to create user - service error' } };
    }
  };

  const signIn = async (email: string, password: string, captchaToken?: string) => {
    // Check rate limiting first
    try {
      const { data: rateLimitData, error: rateLimitError } = await supabase.functions.invoke('auth-rate-limiter', {
        body: { email, action: 'signin' }
      });

      if (rateLimitError || (rateLimitData && rateLimitData.blocked)) {
        return { error: { message: rateLimitData?.error || 'Rate limit exceeded. Please try again later.' } };
      }
    } catch (rateLimitError) {
      console.warn('Rate limiter unavailable, proceeding with signin');
    }

    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
      options: {
        captchaToken
      }
    });
    return { error };
  };

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/`
      }
    });
    return { error };
  };

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    // Clear all cached data when user signs out
    invalidateQuery(['*']);
  }, []);

  const generateReferralCode = async () => {
    if (!user) return { error: 'User not authenticated' };

    try {
      const { data: codeData, error: codeError } = await supabase.rpc('generate_referral_code');
      if (codeError) throw codeError;

      const { error: insertError } = await supabase
        .from('referral_codes')
        .insert({
          code: codeData,
          referrer_user_id: user.id
        });

      if (insertError) throw insertError;
      return { code: codeData, error: null };
    } catch (error) {
      return { error };
    }
  };

  const getReferralCodes = async () => {
    if (!user) return { error: 'User not authenticated' };

    try {
      const { data, error } = await supabase
        .from('referral_codes')
        .select('*')
        .eq('referrer_user_id', user.id)
        .order('created_at', { ascending: false });

      return { codes: data, error };
    } catch (error) {
      return { error };
    }
  };

  const refreshProfile = useCallback(async () => {
    if (user) {
      await fetchProfile(user.id);
    }
  }, [user, fetchProfile]);

  const value = useMemo(() => ({
    user,
    session,
    profile,
    loading,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    generateReferralCode,
    getReferralCodes,
    refreshProfile
  }), [
    user, 
    session, 
    profile, 
    loading, 
    signOut,
    fetchProfile,
    refreshProfile
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}